import { SearchBar } from '@/components/forms/search-bar';
import { cn } from '@/lib/utils';
import type { MenuItem } from '@/types/menu-item';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useState } from 'react';
import { Modal, Pressable, ScrollView, Text, View } from 'react-native';

const RATING_CHIPS = [
  { key: 'not_for_me', label: 'Skip It' },
  { key: 'okay', label: 'Okay' },
  { key: 'try_it', label: 'Try It' },
  { key: 'must_try', label: 'Must Try' },
] as const;

interface ReviewFiltersProps {
  searchText: string;
  onSearchChange: (text: string) => void;
  menuItems: MenuItem[];
  selectedMenuItemIds: string[];
  onMenuItemSelect: (ids: string[]) => void;
  selectedRatingKey?: string;
  onRatingSelect: (key: string | undefined) => void;
  onClearAll: () => void;
}

export function ReviewFilters({
  searchText,
  onSearchChange,
  menuItems,
  selectedMenuItemIds,
  onMenuItemSelect,
  selectedRatingKey,
  onRatingSelect,
  onClearAll,
}: ReviewFiltersProps) {
  const [menuPickerVisible, setMenuPickerVisible] = useState(false);

  const hasAnyFilter = searchText.length > 0 || !!selectedRatingKey || selectedMenuItemIds.length > 0;
  const hasSelection = selectedMenuItemIds.length > 0;

  const dishButtonLabel = (() => {
    if (selectedMenuItemIds.length === 0) return 'All dishes';
    if (selectedMenuItemIds.length === 1) {
      const item = menuItems.find((m) => m.id === selectedMenuItemIds[0]);
      return item?.name ?? '1 dish';
    }
    return `${selectedMenuItemIds.length} dishes`;
  })();

  const toggleMenuItem = (id: string) => {
    if (selectedMenuItemIds.includes(id)) {
      onMenuItemSelect(selectedMenuItemIds.filter((mid) => mid !== id));
    } else {
      onMenuItemSelect([...selectedMenuItemIds, id]);
    }
  };

  return (
    <View className="px-4" style={{ gap: 12 }}>
      <View className="flex-row items-center justify-between mb-1">
        <Text className="text-sm font-medium text-muted-foreground">Filters</Text>
        {hasAnyFilter && (
          <Pressable onPress={onClearAll} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Text className="text-xs font-medium text-primary">Clear all</Text>
          </Pressable>
        )}
      </View>

      <SearchBar
        value={searchText}
        onChangeText={onSearchChange}
        placeholder="Search reviews..."
        height={40}
      />

      {/* Rating chips + menu item button */}
      <View className="flex-row flex-wrap" style={{ gap: 8 }}>
        {RATING_CHIPS.map((chip) => {
          const isActive = selectedRatingKey === chip.key;
          return (
            <Pressable
              key={chip.key}
              onPress={() => onRatingSelect(isActive ? undefined : chip.key)}
              className={cn(
                'px-3 py-1.5 rounded-lg',
                isActive ? 'bg-primary' : 'bg-surface border border-border'
              )}
            >
              <Text
                className={cn(
                  'text-sm font-medium',
                  isActive ? 'text-primary-foreground' : 'text-foreground'
                )}
              >
                {chip.label}
              </Text>
            </Pressable>
          );
        })}

        {/* Menu item picker button */}
        {menuItems.length > 0 && (
          <Pressable
            onPress={() => setMenuPickerVisible(true)}
            className={cn(
              'px-3 py-1.5 rounded-lg flex-row items-center',
              hasSelection ? 'bg-primary' : 'bg-surface border border-border'
            )}
            style={{ gap: 4 }}
          >
            <Text
              className={cn(
                'text-sm font-medium',
                hasSelection ? 'text-primary-foreground' : 'text-foreground'
              )}
              numberOfLines={1}
            >
              {dishButtonLabel}
            </Text>
            <Ionicons
              name="chevron-down"
              size={14}
              className={hasSelection ? 'text-primary-foreground' : 'text-muted-foreground'}
            />
          </Pressable>
        )}
      </View>

      {/* Menu item picker modal */}
      <Modal
        visible={menuPickerVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setMenuPickerVisible(false)}
      >
        <Pressable
          className="flex-1 bg-black/50 justify-end"
          onPress={() => setMenuPickerVisible(false)}
        >
          <View
            className="bg-background rounded-t-2xl max-h-[60%]"
            onStartShouldSetResponder={() => true}
          >
            <View className="px-4 py-3 border-b border-border flex-row items-center justify-between">
              <Text className="text-lg font-semibold text-foreground">Filter by dish</Text>
              <View className="flex-row items-center" style={{ gap: 12 }}>
                {hasSelection && (
                  <Pressable
                    onPress={() => onMenuItemSelect([])}
                    hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                  >
                    <Text className="text-sm font-medium text-primary">Clear</Text>
                  </Pressable>
                )}
                <Pressable
                  onPress={() => setMenuPickerVisible(false)}
                  hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                >
                  <Ionicons name="close" size={24} className="text-muted-foreground" />
                </Pressable>
              </View>
            </View>
            <ScrollView className="px-4 py-2">
              {menuItems.map((item) => {
                const isActive = selectedMenuItemIds.includes(item.id);
                return (
                  <Pressable
                    key={item.id}
                    onPress={() => toggleMenuItem(item.id)}
                    className={cn(
                      'py-3 px-3 rounded-lg mb-1 flex-row items-center justify-between',
                      isActive ? 'bg-primary/10' : ''
                    )}
                  >
                    <Text
                      className={cn(
                        'text-base flex-1',
                        isActive ? 'text-primary font-semibold' : 'text-foreground'
                      )}
                      numberOfLines={1}
                    >
                      {item.name}
                    </Text>
                    {isActive && (
                      <Ionicons name="checkmark" size={20} className="text-primary ml-2" />
                    )}
                  </Pressable>
                );
              })}
            </ScrollView>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
}
